import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { auth, me } from '@/lib/api/endpoints';
import { getLanguage, loadCatalog, setLanguage } from '@/lib/i18n';
import { keys } from '@/lib/queryClient';
import { detectTimezone } from '@/lib/utils';

/**
 * The session.
 *
 * `/auth/status` never returns 401 — "not signed in" is a valid answer — so
 * this query resolves rather than erroring for a signed-out visitor.
 */
export function useAuth() {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: keys.auth,
    queryFn: () => auth.status(),
    // The session is the gate for the whole app; a stale answer here would
    // render the wrong shell entirely.
    staleTime: 0,
    retry: 1,
  });

  return {
    isLoading: isPending,
    isError,
    isAuthenticated: data?.authenticated ?? false,
    needsOnboarding: data?.needs_onboarding ?? false,
    user: data?.user ?? null,
    refetch,
  };
}

/** Full-page navigation: an OAuth redirect cannot be a fetch. */
export function startGoogleLogin() {
  // The language chosen on the sign-in screen seeds a *new* account. An
  // existing one keeps whatever it already has — see the auth route.
  window.location.href = auth.googleLoginUrl(detectTimezone(), getLanguage());
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => auth.logout(),
    onSettled: () => {
      // Drop every cached query: the next user of this browser must not see
      // the previous one's tasks flash before the redirect.
      queryClient.clear();
      window.location.href = '/';
    },
  });
}

export function useSetUsername() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (username: string) => me.setUsername(username),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.auth });
      void queryClient.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useSettings() {
  return useQuery({
    queryKey: keys.settings,
    queryFn: () => me.settings(),
    staleTime: 60_000,
  });
}

/**
 * Adopt the language stored on the account.
 *
 * The account is the authority: the same person on a borrowed machine should
 * get their own language, not the browser's. Until the settings load, the
 * interface runs on the locally detected one — which is why this is an effect
 * and not a render-time decision.
 */
export function useLanguageSync() {
  const { data } = useSettings();
  const accountLanguage = data?.language;

  useEffect(() => {
    if (!accountLanguage || accountLanguage === getLanguage()) return;
    void loadCatalog(accountLanguage).then(() => setLanguage(accountLanguage));
  }, [accountLanguage]);
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: me.updateSettings,
    onSuccess: (updated) => {
      queryClient.setQueryData(keys.settings, updated);
      // Changing the timezone changes what "today" means, and therefore which
      // tasks are overdue — every task list has to be refetched.
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}
